import React from 'react';
import { Box, Link, Typography } from '@mui/material';

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;
const INLINE = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g;

/** Inline bold and links. Text is rendered as React text nodes, never as HTML. */
function inline(text, keyPrefix) {
  return text.split(INLINE).filter((p) => p !== '').map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (/^https?:\/\//.test(part)) {
      return <Link key={key} href={part} target="_blank" rel="noopener noreferrer">{part}</Link>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/** Small, safe formatter for bot replies: bold, headings, bullet and numbered lists, links, line breaks. */
export default function RichText({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };

  lines.forEach((line, i) => {
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) { flush(); list = { type: 'list', ordered, items: [], start: ordered ? Number(numbered[1]) : 1 }; }
      list.items.push(inline(bullet ? bullet[1] : numbered[2], `l${i}`));
      return;
    }
    flush();
    if (!line.trim()) { blocks.push({ type: 'gap', key: i }); return; }
    const heading = HEADING.exec(line);
    blocks.push({ type: heading ? 'heading' : 'p', key: i, content: inline(heading ? heading[1] : line, `p${i}`) });
  });
  flush();

  return (
    <Box sx={{ wordBreak: 'break-word' }}>
      {blocks.map((b, i) => {
        if (b.type === 'gap') return <Box key={`g${b.key}`} sx={{ height: 8 }} />;
        if (b.type === 'heading') return <Typography key={`h${b.key}`} fontWeight={700} sx={{ mt: 0.5 }}>{b.content}</Typography>;
        if (b.type === 'list') {
          return (
            <Box key={`ul${i}`} component={b.ordered ? 'ol' : 'ul'} start={b.ordered ? b.start : undefined} sx={{ my: 0.5, pl: 3 }}>
              {b.items.map((it, j) => <li key={j}>{it}</li>)}
            </Box>
          );
        }
        return <Typography key={`p${b.key}`} sx={{ whiteSpace: 'pre-wrap' }}>{b.content}</Typography>;
      })}
    </Box>
  );
}
